<template>
  <div>
    <form @submit.prevent="next">
      <FormSelect
        v-model="fields.industry"
        name="industry"
        label="What industry are you in?"
        :options="industries"
        noneLabel="- Select an option -"
        :errors="validation.fields.industry.errors"
        :dirty="validation.fields.industry.dirty"
      />
      <FormSelect
        v-model="fields.alreadySelling"
        name="already_selling_products"
        label="Are you already selling products?"
        :options="alreadySellingOptions"
        noneLabel="- Select an option -"
        :errors="validation.fields.alreadySelling.errors"
        :dirty="validation.fields.alreadySelling.dirty"
      />
      <FormSelect
        v-if="
          fields.alreadySelling && fields.alreadySelling === 'selling_online_different_platform'
        "
        v-model="fields.currentPlatform"
        name="platform_currently_using"
        label="Which platform?"
        :options="platformOptions"
        noneLabel="- Select an option -"
        :errors="validation.fields.currentPlatform.errors"
        :dirty="validation.fields.currentPlatform.dirty"
      />
      <FormSelect
        v-if="fields.alreadySelling"
        v-model="fields.howSell"
        name="plan_to_sell"
        label="How do you plan to sell?"
        :options="planSellOptions"
        noneLabel="- Select an option -"
        :errors="validation.fields.howSell.errors"
        :dirty="validation.fields.howSell.dirty"
      />
      <hr />
      <div v-if="fields.howSell && ['only_offline', 'online_and_offline'].includes(fields.howSell)">
        <label>When you sell in person, will you need any of the following?</label>
        <FormCheckbox
          v-model="fields.salesMobileOrder"
          name="in_person_sales_needs[mobile_ordering]"
          label="A way to create orders (mobile ordering)"
        />
        <FormCheckbox
          v-model="fields.salesMobileFunds"
          name="in_person_sales_needs[mobile_point_of_sale]"
          label="A way to collect funds (mobile point of sale)"
        />
        <FormCheckbox
          v-model="fields.salesMobileCatalog"
          name="in_person_sales_needs[mobile_catalog]"
          label="A way to present your products (mobile catalog)"
        />
        <hr />
      </div>
      <FormSelect
        v-model="fields.catalogSize"
        name="product_catalog_size"
        label="How many products are in your product catalog?"
        :options="catalogOptions"
        noneLabel="- Select an option -"
        :errors="validation.fields.catalogSize.errors"
        :dirty="validation.fields.catalogSize.dirty"
      />
      <hr />
      <label>Do your products have variations?</label>
      <FormCheckbox
        v-model="fields.hasVariations"
        name="has_variations[has_variations]"
        label="Yes"
      />
      <hr />
      <label>
        What kind of content do you already have developed? (select all that apply)
      </label>
      <FormCheckbox
        v-model="fields.contentExplainer"
        name="existing_content_types[explainer_video]"
        label="Explainer Video"
      />
      <FormCheckbox
        v-model="fields.contentKeyDifferentiate"
        name="existing_content_types[key_differentiating_features]"
        label="Key Differentiating Features"
      />
      <FormCheckbox
        v-model="fields.contentArticles"
        name="existing_content_types[articles_on_other_sites_telling_your_story]"
        label="Articles on other sites telling your story"
      />
      <FormCheckbox
        v-model="fields.contentCaseStudies"
        name="existing_content_types[case_studies_of_happy_customers]"
        label="Case studies of happy customers"
      />
      <FormCheckbox
        v-model="fields.contentReturnPolicy"
        name="existing_content_types[amazing_return_policy]"
        label="An amazing return policy"
      />
      <FormCheckbox
        v-model="fields.contentSocial"
        name="existing_content_types[social_proof]"
        label="Social Proof (Instagram, Facebook, Twitter)"
      />
      <FormCheckbox
        v-model="fields.contentOffers"
        name="existing_content_types[special_offers]"
        label="Special Offers"
      />
      <FormCheckbox
        v-model="fields.contentCoupons"
        name="existing_content_types[special_coupons]"
        label="Special Coupons"
      />
      <FormCheckbox
        v-model="fields.contentNewsletter"
        name="existing_content_types[email_newsletter_signups]"
        label="Email newsletter signups"
      />
      <FormCheckbox
        v-model="fields.contentReviews"
        name="existing_content_types[customer_reviews]"
        label="Customer reviews"
      />
      <hr />
      <label>Load sample products?</label>
      <FormCheckbox
        v-model="fields.loadProducts"
        name="demo_products[demo_products]"
        label="Yes, please import sample products into my store."
      />
      <div class="text-right">
        <Btn class="mr-2" variant="light" :disabled="loading" @click="back" v-if="enableBack">
          {{ $t("global.back") }}
        </Btn>
        <Btn variant="primary" type="submit" :loading="loading">
          {{ $t("global.next") }}
        </Btn>
      </div>
    </form>
  </div>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormSelect from "@/components/display/form/FormSelect2";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { storeBuilder as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "StoreBuilderForm",
  components: {
    Btn,
    FormCheckbox,
    FormSelect,
  },
  props: {
    enableBack: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const loading = ref(false);
    const error = ref(null);
    const storeBuilderFields = store.state.checkout.storeBuilderFields;
    const fields = reactive({
      industry: storeBuilderFields.industry,
      alreadySelling: storeBuilderFields.alreadySelling,
      howSell: storeBuilderFields.howSell,
      currentPlatform: storeBuilderFields.currentPlatform,
      salesMobileOrder: storeBuilderFields.salesMobileOrder,
      salesMobileFunds: storeBuilderFields.salesMobileFunds,
      salesMobileCatalog: storeBuilderFields.salesMobileCatalog,
      catalogSize: storeBuilderFields.catalogSize,
      hasVariations: storeBuilderFields.hasVariations,
      contentExplainer: storeBuilderFields.contentExplainer,
      contentKeyDifferentiate: storeBuilderFields.contentKeyDifferentiate,
      contentArticles: storeBuilderFields.contentArticles,
      contentCaseStudies: storeBuilderFields.contentCaseStudies,
      contentReturnPolicy: storeBuilderFields.contentReturnPolicy,
      contentSocial: storeBuilderFields.contentSocial,
      contentOffers: storeBuilderFields.contentOffers,
      contentCoupons: storeBuilderFields.contentCoupons,
      contentNewsletter: storeBuilderFields.contentNewsletter,
      contentReviews: storeBuilderFields.contentReviews,
      loadProducts: storeBuilderFields.loadProducts,
    });

    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        industry: {
          required: "Industry is required",
        },
        alreadySelling: {
          required: "Tell us whether you are already selling",
        },
        currentPlatform: {
          requiredIf: "Current platform is required",
        },
        howSell: {
          requiredIf: "Tell us how you are selling",
        },
        catalogSize: {
          required: "Catalog size required",
        },
      }
    );

    const industries = [
      {
        value: "apparel",
        label: "Apparel",
      },
      {
        value: "books",
        label: "Books / Music / Video",
      },
      {
        value: "electronics",
        label: "Electronics",
      },
      {
        value: "gifts",
        label: "Gifts",
      },
      {
        value: "health-beauty",
        label: "Health / Beauty",
      },
      {
        value: "home-goods",
        label: "Home Goods",
      },
      {
        value: "jewelry",
        label: "Jewelry",
      },
      {
        value: "petcare",
        label: "Petcare",
      },
      {
        value: "sports-hobbies",
        label: "Sports / Hobbies",
      },
      {
        value: "other",
        label: "Other",
      },
    ];

    const alreadySellingOptions = [
      {
        value: "selling_offline",
        label: "I'm selling offline right now",
      },
      {
        value: "selling_online_different_platform",
        label: "I'm selling online with a different platform",
      },
      {
        value: "not_selling_yet",
        label: "I'm not selling yet",
      },
    ];

    const planSellOptions = [
      {
        value: "only_online",
        label: "Only online",
      },
      {
        value: "only_offline",
        label: "Only offline",
      },
      {
        value: "online_and_offline",
        label: "Online and offline",
      },
    ];

    const platformOptions = [
      {
        value: "prestashop",
        label: "PrestaShop",
      },
      {
        value: "etsy",
        label: "Etsy",
      },
      {
        value: "squarespace",
        label: "Squarespace",
      },
      {
        value: "square",
        label: "Square",
      },
      {
        value: "ebay",
        label: "eBay",
      },
      {
        value: "amazon-store",
        label: "Amazon Store",
      },
      {
        value: "bigcommerce",
        label: "BigCommerce",
      },
      {
        value: "wix",
        label: "Wix",
      },
      {
        value: "vend",
        label: "Vend",
      },
      {
        value: "big-cartel",
        label: "Big Cartel",
      },
      {
        value: "magento",
        label: "Magento",
      },
      {
        value: "volusion",
        label: "Volusion",
      },
      {
        value: "woocommerce",
        label: "WooCommerce",
      },
      {
        value: "other",
        label: "Other",
      },
    ];

    const catalogOptions = [
      {
        value: 1,
        label: "1 product",
      },
      {
        value: 2,
        label: "2 - 99 products",
      },
      {
        value: 100,
        label: "100+ products",
      },
    ];

    const next = () => {
      store.dispatch("checkout/setStoreBuilderFields", { ...fields });
      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("checkout/getStoreBuilderId")
            .then(() => {
              emit("success");
            })
            .catch((e) => {
              console.log(e);
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch((e) => e);
    };

    const back = () => {
      store.dispatch("checkout/setStoreBuilderFields", { ...fields });
      emit("back");
    };

    return {
      industries,
      alreadySellingOptions,
      planSellOptions,
      platformOptions,
      catalogOptions,
      fields,
      next,
      loading,
      error,
      back,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped></style>
